/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import Point from '@arcgis/core/geometry/Point';
import Graphic from '@arcgis/core/Graphic';
import debounce from 'lodash.debounce';
import {normalizeSCIDevice} from '@ion/core/src/services/ArcGISUtils';
import Device from '@ion/core/src/services/Device';
import {CVSAssetType, GenericObject} from '@ion/core/src/types/entities';
import {
  createCircleSymbol
} from '@ion/dls/src/maps/symbols/CircleSymbol';
import {
  createDeviceSymbol
} from '@ion/dls/src/maps/symbols/DeviceSymbol';
import {
  createSignalSymbol
} from '@ion/dls/src/maps/symbols/SignalSymbol';
import {
  IDENTIFIER_PROPERTY,
  IDENTIFIER_TYPE
} from '../constants';
import {
  esriTypeCast,
  getFeatures,
  getItemQuery,
  getLayerEffectQuery,
  noneSelectedQuery
} from './MapViewUtils';

// Defines the props definitions for the Add / Update method
type AddUpdateProps = {
  layer: __esri.Feature;
  data: GenericObject;
  create: Function;
  addUpdate: Function;
};

type HighlightGraphicProps = {
  layer: __esri.FeatureLayer,
  selectedAsset: CVSAssetType,
  highlightItem: Function,
  removeHighlight: Function,
  createSelectionEffect: Function
};

/**
 * Defines a utility to return the renderer of the Fleet Layer
 */
export const getFleetLayerRenderer = () => {
  return {
    type   : 'simple',
    symbol : {
      type : 'cim',
      data : {
        type   : 'CIMSymbolReference',
        symbol : {
          type         : 'CIMPointSymbol',
          symbolLayers : [
            createSignalSymbol(),
            createDeviceSymbol(),
            createCircleSymbol()
          ]
        }
      }
    }
  };
};

/**
 * Defines a utility to create and return array of graphics
 *
 *  ```typescript
 *
 * import {createGraphics} from '@ion/dls/src/maps/helpers'
 *
 * const graphics = createGraphics(features);
 *
 * ```
 */
export const createGraphics = (features: any, mapFields?: GenericObject[]) => {
  if (features?.length) {
    const graphics = [];
    features?.forEach((featureAsDevices: Device) => {
      const feature   = normalizeSCIDevice(esriTypeCast(getFeatures(featureAsDevices), mapFields));
      const longitude = feature.longitude ?? feature?.geometry?.y ?? 0;
      const latitude  = feature.latitude ?? feature?.geometry?.x ?? 0;
      if (!latitude && !longitude) {
        feature.heading = 0;
      }
      const point   = new Point({longitude, latitude, spatialReference: {wkid: 4326}});
      const graphic = new Graphic({geometry: point, symbol: null, attributes: feature});
      graphics.push(graphic);
    });
    return graphics;
  }
  return [];
};

/**
 * Defines a utility to Add / Update graphics to the layer
 */
export const addGraphicsToLayer = ({layer, data, create, addUpdate}: AddUpdateProps) => {
  if (!layer) {
    data?.length && create(data);
  } else {
    addUpdate(data);
  }
};

/**
 * Defines a utility to return the highlight params based on the selected asset.
 * These params are necessary for highlight a graphic on the Layer.
 * 
 * ```typescript
 *
 * import {getHighlightParams} from '@ion/dls/src/maps/helpers/LayerUtils'
 *
 * const params = getHighlightParams(selectedAsset);
 *
 * ```
 */
export const getHighlightParams = (selectedAsset: CVSAssetType) => {
  if (selectedAsset) {
    const query = getItemQuery({
      item         : selectedAsset,
      property     : IDENTIFIER_PROPERTY,
      defaultQuery : noneSelectedQuery
    });
    const params = {
      selectedItem : selectedAsset,
      type         : IDENTIFIER_TYPE,
      query
    };
    return params;
  }
  return null;
};

/**
 * Defines a utility to highlight a graphic on given layer.
 * 
 * ```typescript
 *
 * import {highlightGraphic} from '@ion/dls/src/maps/helpers/LayerUtils'
 *
 * highlightGraphic(FleetAssetLayer)
 *
 * ```
 */
export const highlightGraphic = debounce(({
  layer,
  selectedAsset,
  highlightItem,
  removeHighlight,
  createSelectionEffect
}: HighlightGraphicProps) => {
  if (selectedAsset) {
    const params = getHighlightParams(selectedAsset);
    const where  = getLayerEffectQuery(params);
    removeHighlight(layer);
    highlightItem(layer, where);
    createSelectionEffect(layer, where);
  } else if (layer?.featureEffect) {
    removeHighlight(layer);
  }
}, 135);
